import type { QueryClient } from '@tanstack/react-query'
import { queryOptions } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { initialPlanningTasks } from '@/features/tasks/model/initial-tasks'
import { api, unwrap } from './api'
import { authClient } from './auth'

export const workspaceKeys = {
  all: ['workspaces'] as const,
  detail: (slug: string) => ['workspace', slug] as const,
}

/**
 * Workspaces are Better Auth organizations, so the list comes from its client
 * rather than our API. Route guards need it outside React, which is why this
 * exists alongside `authClient.useListOrganizations()` — the hook covers
 * components, these cover `beforeLoad`.
 */
export const workspacesQueryOptions = queryOptions({
  queryKey: workspaceKeys.all,
  queryFn: async () => {
    const { data } = await authClient.organization.list()
    return data ?? []
  },
  staleTime: 5 * 60 * 1000,
})

export function ensureWorkspaces(queryClient: QueryClient) {
  return queryClient.ensureQueryData({ ...workspacesQueryOptions, revalidateIfStale: true })
}

export type EnsureDefaultWorkspaceResponse = InferResponseType<typeof api.api.workspaces.$post, 201>

function ensureDefaultWorkspace() {
  return unwrap<EnsureDefaultWorkspaceResponse>(
    api.api.workspaces.$post({
      json: {
        name: 'Moje svatba',
        weddingName: 'Nová svatba',
        slug: 'moje-svatba',
        initialTasks: initialPlanningTasks,
      },
    }),
  )
}

export async function ensureFirstWorkspaceSlug(queryClient: QueryClient): Promise<string> {
  const { workspace } = await queryClient.fetchQuery({
    queryKey: [...workspaceKeys.all, 'initialize'],
    queryFn: ensureDefaultWorkspace,
    staleTime: 0,
  })
  await refreshWorkspaces(queryClient)

  return workspace.slug
}

/**
 * Call after workspace membership changes, and await it before navigating.
 * Removing rather than invalidating mirrors `refreshSession`: nothing may be
 * observing this query yet, so an invalidated entry could still be handed back
 * stale during the next route guard.
 */
export async function refreshWorkspaces(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: workspacesQueryOptions.queryKey })
  await queryClient.ensureQueryData(workspacesQueryOptions)
}

export type WorkspaceResponse = InferResponseType<(typeof api.api.workspaces)[':slug']['$get'], 200>

/**
 * The workspace behind a URL slug, with the caller's role. The API answers 404
 * both for an unknown slug and for one the caller does not belong to, so this
 * never has to tell them apart.
 */
export const workspaceQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: workspaceKeys.detail(slug),
    queryFn: () => unwrap<WorkspaceResponse>(api.api.workspaces[':slug'].$get({ param: { slug } })),
    staleTime: 5 * 60 * 1000,
  })
